import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation } from 'Shared/Hooks/useApolloClient'
import { loadStripeTerminal } from '@stripe/terminal-js'
import { asyncHandler } from 'Utils/asyncHelpers'
import { notifyError } from 'Utils/errorNotifications'
import * as StripeTerminalMock from 'Utils/StripeTerminalMock'
import stripeTerminalTokenCreateMutation from './Queries/stripeTerminalTokenCreateMutation'

const TERMINAL_CONNECTED = 'connected'
const TERMINAL_NOT_CONNECTED = 'not_connected'

const useStripeTerminal = () => {
  const [terminal, setTerminal] = useState()
  const [connectionStatus, setConnectionStatus] = useState(TERMINAL_NOT_CONNECTED)
  const [error, setError] = useState()
  const [isConnecting, setIsConnecting] = useState(false)
  const [navigatorPermission, setNavigatorPermission] = useState(null)
  const [createStripeTerminalToken] = useMutation(stripeTerminalTokenCreateMutation)
  const terminalState = useSelector(state => state.campground?.terminal || state.invoices?.campground?.terminal)
  const { readerKey, testCardNumber, useMock, useSimulation } = terminalState || {}

  useEffect(() => {
    getTerminal()
  }, [])

  useEffect(() => {
    fetchNavigatorPermissions()
  }, [navigator])

  useEffect(() => {
    if (terminal && useSimulation) terminal.setSimulatorConfiguration({ testCardNumber })

    if (terminal && readerKey) connectReaderHandler()
  }, [readerKey, terminal, useSimulation])

  const clearError = () => setError(null)

  const connectReaderHandler = asyncHandler(async () => {
    if (terminal) {
      setIsConnecting(true)

      const discoverResult = await terminal.discoverReaders({ simulated: useSimulation })
      if (discoverResult.error) {
        handleError(`Failed to discover: ${discoverResult.error}`)
        setIsConnecting(false)
        return
      }

      if (discoverResult.discoveredReaders.length === 0) {
        handleError('No available readers.')
        setIsConnecting(false)
        return
      }

      selectReader(discoverResult)
    }
  })

  const fetchNavigatorPermissions = async () => {
    if (navigator.permissions)
      try {
        const result = await navigator.permissions.query({ name: 'local-network-access' })
        setNavigatorPermission(result?.state)
      } catch (e) {
        handleError(`Permission to access local network denied. Terminal cannot connect to the reader.
                     Please enable local network access and try again.`)
      }
  }

  const fetchConnectionToken = asyncHandler(async () => {
    const response = await createStripeTerminalToken()
    const { token, error } = await response.data.stripeTerminalTokenCreate
    if (error) return handleError(`Connection Token Fetch Error: ${error}`)

    return token
  })

  const getTerminal = asyncHandler(async () => {
    if (useMock) return setTerminal(StripeTerminalMock)

    const StripeTerminal = await loadStripeTerminal()

    const terminalReal = StripeTerminal.create({
      onFetchConnectionToken: fetchConnectionToken,
      onUnexpectedReaderDisconnect: unexpectedDisconnect,
    })

    setTerminal(terminalReal)
  })

  const handleError = errorMsg => {
    setError(errorMsg)
    notifyError(errorMsg)
  }

  const isConnected = connectionStatus === TERMINAL_CONNECTED

  const selectReader = asyncHandler(async discoverResult => {
    const readers = discoverResult.discoveredReaders
    const selectedReader = readers.find(reader => reader.id === readerKey)

    if (!selectedReader) {
      handleError('Could not find registered reader.')
      setIsConnecting(false)
      return
    }

    const connectResult = await terminal.connectReader(selectedReader, { failIfInUse: true })

    if (connectResult.error) {
      handleError(`Failed to connect: ${connectResult.error.message}`)
      setIsConnecting(false)
      return
    }

    setConnectionStatus(TERMINAL_CONNECTED)
    setIsConnecting(false)
  })

  const unexpectedDisconnect = () => {
    handleError('The card reader is offline. Please check the connection and try again.')
    setConnectionStatus(TERMINAL_NOT_CONNECTED)
  }

  return { clearError, error, getTerminal, handleError, isConnected, isConnecting, navigatorPermission, terminal }
}

export default useStripeTerminal
